import { CaretDownIcon, CaretUpIcon, InfoIcon } from "@phosphor-icons/react";
import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import CustomSwitch from "../../common/general/CustomSwitch/CustomSwitch";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CourseSettingsAccordion from "../CourseSettingsAccordion/CourseSettingsAccordion";
import { colors, radii, semanticColors, spacing, typographyScale } from "../../../styles/design-system";

import { NudgeCategoryDefinition, NudgeTypeSettings } from "./nudgeSettingsConfig";

type NudgeSettingsProps = {
  categories: NudgeCategoryDefinition[];
  nudgeTypeSettings: NudgeTypeSettings;
  onToggleType: (type: keyof NudgeTypeSettings, enabled: boolean) => void;
};

function NudgeSettings({ categories, nudgeTypeSettings, onToggleType }: NudgeSettingsProps) {
  const [expandedCategoryKey, setExpandedCategoryKey] = useState<string | null>(categories[0]?.key ?? null);

  const totalAvailable = categories.reduce((sum, category) => sum + category.items.length, 0);
  const activeNudges = Object.values(nudgeTypeSettings).filter(Boolean).length;
  const activeCategories = categories.filter((category) =>
    category.items.some((item) => nudgeTypeSettings[item.type])
  ).length;

  const categorySummaries = useMemo(
    () =>
      categories.map((category) => {
        const activeCount = category.items.filter((item) => nudgeTypeSettings[item.type]).length;
        const allEnabled = activeCount === category.items.length;
        const partiallyEnabled = activeCount > 0 && !allEnabled;

        return {
          ...category,
          activeCount,
          allEnabled,
          partiallyEnabled,
        };
      }),
    [categories, nudgeTypeSettings]
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerCard}>
        <View style={styles.headerRow}>
          <View style={styles.headerIcon}>
            <InfoIcon size={18} color={colors.brand[500]} weight="regular" />
          </View>
          <View style={styles.headerContent}>
            <CustomText
              text="Nudge Settings"
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.headerTitle}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text="Control which notification types are available to students. Disabled nudges will be removed from future nudge decisioning for this institution."
              size="s"
              useTranslationText={false}
              style={styles.headerDescription}
            />
          </View>
        </View>

        <View style={styles.statsCard}>
          <View style={styles.statItem}>
            <CustomText text={String(activeCategories)} size="xxl" weight="bold" useTranslationText={false} />
            <CustomText text="Active Categories" size="xs" useTranslationText={false} style={styles.statLabel} />
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <CustomText text={String(activeNudges)} size="xxl" weight="bold" useTranslationText={false} />
            <CustomText text="Active Nudges" size="xs" useTranslationText={false} style={styles.statLabel} />
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <CustomText text={String(totalAvailable)} size="xxl" weight="bold" useTranslationText={false} />
            <CustomText text="Total Available" size="xs" useTranslationText={false} style={styles.statLabel} />
          </View>
        </View>
      </View>

      <CustomSpacing type="vertical" size="xl" />

      {categorySummaries.map((category) => {
        const Icon = category.icon;
        const isExpanded = expandedCategoryKey === category.key;

        return (
          <CourseSettingsAccordion
            key={category.key}
            icon={
              <View style={[styles.categoryIcon, { backgroundColor: category.iconBackground }]}>
                <Icon size={20} color={category.iconColor} weight="regular" />
              </View>
            }
            title={category.title}
            description={category.description}
            expanded={isExpanded}
            onToggle={() => setExpandedCategoryKey(isExpanded ? null : category.key)}
            contentContainerStyle={styles.categoryContentContainer}
            rightContent={
              <View style={styles.categoryActions}>
                <CustomSwitch
                  value={category.allEnabled}
                  onValueChange={(nextValue) => {
                    category.items.forEach((item) => onToggleType(item.type, nextValue));
                  }}
                  onPress={(event) => {
                    event.stopPropagation();
                  }}
                  accessibilityLabel={`${category.title} toggle`}
                  trackStyle={({ checked }) => (category.partiallyEnabled && !checked ? styles.partialTrack : null)}
                />
                <Pressable
                  onPress={(event) => {
                    event.stopPropagation();
                    setExpandedCategoryKey(isExpanded ? null : category.key);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`${isExpanded ? "Collapse" : "Expand"} ${category.title}`}
                  style={styles.expandButton}>
                  {isExpanded ? (
                    <CaretUpIcon size={18} color={colors.grey[500]} weight="bold" />
                  ) : (
                    <CaretDownIcon size={18} color={colors.grey[500]} weight="bold" />
                  )}
                </Pressable>
              </View>
            }>
            <View style={styles.categoryTitleRow}>
              <View style={styles.categoryBadge}>
                <CustomText
                  text={`${category.activeCount}/${category.items.length} active`}
                  size="xs"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.categoryBadgeText}
                />
              </View>
            </View>
            <CustomSpacing type="vertical" size="sm" />
            <View style={styles.itemList}>
              {category.items.map((item, index) => (
                <View
                  key={item.type}
                  style={[styles.itemRow, index < category.items.length - 1 ? styles.itemRowBorder : null]}>
                  <View style={styles.itemText}>
                    <CustomText
                      text={item.label}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.itemTitle}
                    />
                    <CustomSpacing type="vertical" size="xs" />
                    <CustomText
                      text={item.description}
                      size="xs"
                      useTranslationText={false}
                      style={styles.itemDescription}
                    />
                  </View>
                  <CustomSwitch
                    value={nudgeTypeSettings[item.type]}
                    onValueChange={(nextValue) => onToggleType(item.type, nextValue)}
                    accessibilityLabel={`${item.label} toggle`}
                  />
                </View>
              ))}
            </View>
          </CourseSettingsAccordion>
        );
      })}

      <View style={styles.helpCard}>
        <InfoIcon size={18} color="#075985" weight="regular" style={styles.helpIcon} />
        <View style={styles.helpContent}>
          <CustomText
            text="How Nudge Settings Work"
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.helpTitle}
          />
          <CustomSpacing type="vertical" size="xs" />
          <CustomText
            text="• Disabled categories switch off all nudges in that group for your institution"
            size="s"
            useTranslationText={false}
            style={styles.helpListItem}
          />
          <CustomText
            text="• Expanded categories let you disable individual nudge types without affecting others"
            size="s"
            useTranslationText={false}
            style={styles.helpListItem}
          />
          <CustomText
            text="• Changes take effect after you save settings at the bottom of the page"
            size="s"
            useTranslationText={false}
            style={styles.helpListItem}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerCard: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.sm,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: radii.lg,
    backgroundColor: colors.brand[50],
    alignItems: "center",
    justifyContent: "center",
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    color: colors.grey[900],
  },
  headerDescription: {
    color: colors.grey[600],
  },
  statsCard: {
    marginTop: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[50],
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    flexDirection: "row",
    alignItems: "center",
  },
  statItem: {
    flex: 1,
  },
  statLabel: {
    color: colors.grey[500],
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: colors.grey[200],
    marginHorizontal: spacing.sm,
  },
  categoryIcon: {
    width: 40,
    height: 40,
    borderRadius: radii.lg,
    alignItems: "center",
    justifyContent: "center",
  },
  categoryTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    flexWrap: "wrap",
  },
  categoryBadge: {
    backgroundColor: colors.grey[100],
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  categoryBadgeText: {
    color: colors.grey[700],
  },
  categoryActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  expandButton: {
    padding: 2,
  },
  categoryContentContainer: {
    padding: spacing.lg,
  },
  itemList: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingTop: spacing.sm,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  itemRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  itemText: {
    flex: 1,
  },
  itemTitle: {
    color: semanticColors.typography.textDark,
  },
  itemDescription: {
    color: semanticColors.typography.textLight,
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  helpCard: {
    marginTop: spacing.sm,
    backgroundColor: colors.blue[100],
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.blue[500],
    padding: spacing.lg,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.sm,
  },
  helpIcon: {
    marginTop: 2,
  },
  helpContent: {
    flex: 1,
  },
  helpTitle: {
    color: colors.grey[900],
  },
  helpListItem: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    marginBottom: 6,
  },
  partialTrack: {
    backgroundColor: colors.brand[200],
  },
});

export default NudgeSettings;
